import { useEffect, useRef } from "react";

/**
 * Infinite scroll: a ref for a sentinel element, and `load` called whenever it scrolls into view
 * while `enabled` (there is a next page and none is being fetched). The page keeps a "Load more"
 * button beside it for browsers and tests without IntersectionObserver.
 */
export function useLoadMore<T extends Element>(enabled: boolean, load: () => void) {
  const ref = useRef<T | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const node = ref.current;
    if (!enabled || !node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadRef.current();
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled]);

  return ref;
}
